import React, { useState, useRef, useEffect } from 'react';
import { FiGlobe, FiMenu, FiX } from 'react-icons/fi';

/** Read the language Google Translate stored in its cookie (/en/sv → 'sv') */
function getActiveGoogleLang(): string {
  try {
    const match = document.cookie.match(/googtrans=\/[a-z-]+\/([a-z-]+)/);
    if (match && match[1] && match[1] !== 'en') return match[1];
  } catch {}
  return localStorage.getItem('siteLang') || 'en';
}

export default function Header() {
  const [lang, setLang] = useState<string>(() => getActiveGoogleLang());
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  const langs = [
    { code: 'en', label: 'EN', display: 'English' },
    { code: 'sv', label: 'SV', display: 'Svenska' },
    { code: 'es', label: 'ES', display: 'Español' },
    { code: 'de', label: 'DE', display: 'Deutsch' },
  ];

  const changeLanguage = (code: string) => {
    setLang(code);
    setOpen(false);
    localStorage.setItem('siteLang', code);
    // Write the googtrans cookie so it survives page navigation
    const domain = window.location.hostname;
    if (code === 'en') {
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
      localStorage.removeItem('siteLang');
    } else {
      document.cookie = `googtrans=/en/${code}; path=/`;
      document.cookie = `googtrans=/en/${code}; path=/; domain=${domain}`;
    }
    setTimeout(() => {
      const combo = document.querySelector('.goog-te-combo') as HTMLSelectElement | null;
      if (combo) {
        combo.value = code;
        combo.dispatchEvent(new Event('change'));
      }
    }, 150);
  };

  const navigateTo = (e: React.MouseEvent<HTMLAnchorElement>, to: string) => {
    e.preventDefault();
    if (window.location.pathname === to) return;
    window.history.pushState({}, '', to);
    window.dispatchEvent(new Event('app:navigate'));
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-emerald-900/10 bg-[#F7FCF9]/90 backdrop-blur-xl">
      <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#D97706] via-[#14B8A6] to-[#166534]" />
      <div className="max-w-[1360px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a
            href="/"
            onClick={(e) => navigateTo(e, '/')}
            className="notranslate text-xl font-extrabold tracking-tight"
            translate="no"
          >
            <span className="notranslate text-[#0B2F2A]" translate="no">personalfinance</span>
            <span className="notranslate text-[#D97706]" translate="no">care.com</span>
          </a>

          <nav className="hidden md:flex items-center gap-8">
            <a
              href="/"
              onClick={(e) => navigateTo(e, '/')}
              className="relative text-sm font-bold text-[#1C362F] hover:text-[#0B2F2A] transition-colors after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-0 hover:after:w-full after:bg-[#D97706] after:transition-all"
            >
              HOME
            </a>
            <a
              href="/faqs/"
              onClick={(e) => navigateTo(e, '/faqs/')}
              className="relative text-sm font-bold text-[#1C362F] hover:text-[#0B2F2A] transition-colors after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-0 hover:after:w-full after:bg-[#D97706] after:transition-all"
            >
              FAQS
            </a>

            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setOpen(v => !v)}
                className="flex items-center gap-1.5 text-sm font-bold text-[#1C362F] hover:text-[#0B2F2A] transition-colors"
                aria-haspopup="true"
                aria-expanded={open}
                aria-label={`Language selector, current language: ${langs.find(l => l.code === lang)?.display ?? 'English'}`}
                translate="no"
              >
                <FiGlobe className="w-4 h-4 text-[#0F766E]" />
                <span>{langs.find(l => l.code === lang)?.label ?? 'EN'}</span>
              </button>

              {open && (
                <div className="absolute right-0 mt-5 w-36 overflow-hidden rounded-xl border border-emerald-900/10 bg-white py-2 shadow-2xl shadow-emerald-950/10 z-50">
                  {langs.map(l => (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => changeLanguage(l.code)}
                      aria-label={`Select language ${l.display}`}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                        lang === l.code ? 'bg-emerald-50 text-[#0B2F2A] font-bold' : 'text-[#1C362F] hover:bg-emerald-50'
                      }`}
                      translate="no"
                    >
                      {l.display}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <button
            type="button"
            onClick={() => setMobileOpen(v => !v)}
            aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileOpen}
            className="md:hidden p-2 text-[#1C362F] hover:text-[#0B2F2A]"
          >
            {mobileOpen ? <FiX className="w-5 h-5" /> : <FiMenu className="w-5 h-5" />}
          </button>
        </div>

        {mobileOpen && (
          <nav className="md:hidden pb-4 pt-2 space-y-2 border-t border-emerald-900/10">
            <a
              href="/"
              onClick={(e) => navigateTo(e, '/')}
              className="block px-3 py-2 text-sm font-bold text-[#1C362F] hover:bg-emerald-50 rounded-lg"
            >
              HOME
            </a>
            <a
              href="/faqs/"
              onClick={(e) => navigateTo(e, '/faqs/')}
              className="block px-3 py-2 text-sm font-bold text-[#1C362F] hover:bg-emerald-50 rounded-lg"
            >
              FAQS
            </a>
            <div className="flex gap-2 px-3 pt-1 flex-wrap">
              {langs.map(l => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => { changeLanguage(l.code); setMobileOpen(false); }}
                  aria-label={`Switch language to ${l.display}`}
                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                    lang === l.code ? 'bg-[#0F766E] text-white border-[#0F766E]' : 'border-emerald-900/20 text-[#1C362F] hover:border-[#D97706]'
                  }`}
                  translate="no"
                >{l.display}</button>
              ))}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
